// ============================================================
// TILT — tiltOnline.js                    THE MARK + PLAYING WITH FRIENDS
//
// caP'n, 2026-10-25: the Mark goes into every main game. Borrowed whole from Moonshine
// (moonshineOnline.js, itself from Hooked) - the borrow-first rule:
//   · ONE SOUL: ieIdentity (vendored byte-identical from gamemaster/engine). Signed OUT, Tilt is the
//     same solo machine it always was (progression.js keeps its own save); signed IN, you carry your
//     Mark here like everywhere else. NOTHING here writes a score, a token or a currency.
//   · THE FRIENDS PANEL + THE MARK: IEFriends / Mark / MarkEditor (vendored). Not re-implemented.
// This file is only Tilt's part: the sign-in card in Tilt's colours, and the typing law.
// ============================================================
import { useState, useEffect } from "react";
import { UI, PANEL, RAISED } from "./theme";

export const GAME_ID = "tilt";

// a key pressed while typing a name or a password - or while a sign-in / Mark card is up - must never
// flick a flipper, fire a power or pull the plunger. App.js sets ONLINE_MODAL.open; the key handler asks typing(e).
export const ONLINE_MODAL = { open: false };
// A Mark opened from the FRIENDS PANEL isn't App's (the dock is App's sibling), so look for it on the page:
// every Mark / MarkEditor card draws a .mk-tab or .mk-scroll (Mark.js). Tilt-only; the shared Mark is untouched.
const markUp = () => { try { return !!document.querySelector(".mk-tab, .mk-scroll"); } catch (err) { return false; } };
export const typing = (e) => {
  if (ONLINE_MODAL.open || markUp()) return true;
  const t = e && e.target; return !!t && (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable);
};

// ── SIGNING IN (so you can play with friends) ── Tilt never needs an account to play solo:
// this card only opens when you ask for company. Same soul as the Pier and Terror.
export function SignInCard({ auth, onClose }) {
  const [mode, setMode] = useState(auth.phase === "name" ? "name" : "in");
  const [email, setEmail] = useState(auth.guestEmail || "");
  const [pw, setPw] = useState("");
  const [name, setName] = useState("");
  const [msg, setMsg] = useState(null);
  // after a good answer, the soul's phase decides: named -> done; signed in but nameless -> ask the name right here
  const [waiting, setWaiting] = useState(false);
  useEffect(() => {
    if (!waiting) return;
    if (auth.phase === "ready") onClose();
    else if (auth.phase === "name") { setMode("name"); setWaiting(false); }
  }, [waiting, auth.phase, onClose]);
  const go = async (e) => {
    e.preventDefault(); setMsg(null);
    const r = mode === "name" ? await auth.claimName(name)
      : mode === "up" ? await auth.signUp(email, pw, name)
      : await auth.signIn(email, pw);
    if (r && r.ok) setWaiting(true); else setMsg((r && r.error) || auth.error || "That didn't work.");
  };
  const field = {
    width: "100%", boxSizing: "border-box", marginTop: 8, padding: "10px 12px", borderRadius: 9,
    background: UI.pageDeep, color: UI.text, border: `1px solid ${UI.panelEdge}`, fontFamily: UI.font, fontSize: 15, outline: "none",
  };
  const stop = (e) => e.stopPropagation();
  return (
    <div onKeyDown={(e) => { stop(e); if (e.key === "Escape") onClose(); }} onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 880, display: "grid", placeItems: "center", background: "rgba(0,0,0,0.6)" }}>
      <form onSubmit={go} style={{
        width: "min(340px, calc(100vw - 32px))", padding: "22px 20px 16px", borderRadius: 14,
        background: PANEL, border: `1px solid ${UI.panelEdge}`, boxShadow: RAISED + ", 0 18px 60px rgba(0,0,0,0.75)",
        fontFamily: UI.font, color: UI.text,
      }}>
        <div style={{ fontFamily: UI.fontD, fontSize: 17, letterSpacing: 3, color: UI.brassBright, textAlign: "center" }}>
          {mode === "name" ? "WHAT DO WE CALL YOU?" : "PLAY WITH FRIENDS"}
        </div>
        <div style={{ fontSize: 14, color: UI.textDim, textAlign: "center", marginTop: 4 }}>
          {mode === "name" ? "One name, every game." : "Same you as the Pier and Terror."}
        </div>
        {mode !== "name" && <input style={field} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />}
        {mode !== "name" && <input style={field} type="password" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} />}
        {mode !== "in" && <input style={field} placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} maxLength={24} autoFocus={mode === "name"} />}
        {msg && <div style={{ color: UI.bad, fontSize: 13, marginTop: 8, textAlign: "center" }}>{msg}</div>}
        <button type="submit" disabled={auth.busy} style={{
          width: "100%", marginTop: 14, padding: "11px 0", borderRadius: 9, cursor: "pointer",
          fontFamily: UI.fontD, fontSize: 12, letterSpacing: 2.4, color: "#1a120a", background: UI.brassBright, border: "none",
        }}>{mode === "name" ? "THAT'S ME" : mode === "up" ? "MAKE ONE" : "SIGN IN"}</button>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, fontSize: 13 }}>
          {mode !== "name"
            ? <span onClick={() => setMode(mode === "in" ? "up" : "in")} style={{ color: UI.brass, cursor: "pointer" }}>{mode === "in" ? "New here? Make one" : "Have one? Sign in"}</span>
            : <span />}
          <span onClick={onClose} style={{ color: UI.textDim, cursor: "pointer" }}>Play alone</span>
        </div>
      </form>
    </div>
  );
}
